import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { MapPin, Shield, Plus, Heart, X, CheckCircle2, Hourglass, Inbox, Send, Phone, Mail } from "lucide-react";
import PhotoUpload from "../components/PhotoUpload";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "dog", label: "Dogs" },
  { id: "cat", label: "Cats" },
  { id: "rabbit", label: "Rabbits" },
];

export default function Adoption() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState("browse"); // browse | mine | received | sent
  const [sp, setSp] = useState("all");
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [applying, setApplying] = useState(null);

  // If we arrived from MyPets via "Give for adoption", auto-open the form with that pet's details pre-filled.
  useEffect(() => {
    const pet = location.state?.prefillFromPet;
    if (pet) {
      setPrefill(pet);
      setCreating(true);
      // Clear router state so a refresh / back-navigation doesn't reopen the modal.
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const loadBrowse = () => axios.get(`${API}/adoption`, { params: sp === "all" ? {} : { species: sp } }).then(r => setItems(r.data));
  useEffect(() => { if (tab === "browse") loadBrowse(); }, [tab, sp]);

  return (
    <div className="space-y-6" data-testid="adoption-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Adopt · Find your best friend</h1>
          <p className="text-ink-soft">Verified rescues. Loving families. Lifelong bonds.</p>
        </div>
        {user && (
          <button onClick={() => setCreating(true)} data-testid="list-pet-btn" className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4"/> List a pet
          </button>
        )}
      </header>

      <nav className="flex gap-2 overflow-x-auto no-scrollbar border-b border-line pb-1">
        {[
          { id: "browse", label: "Browse pets" },
          { id: "mine", label: "My listings" },
          { id: "received", label: "Applications received", icon: Inbox },
          { id: "sent", label: "My applications", icon: Send },
        ].map(t => (
          <button key={t.id} data-testid={`adopt-tab-${t.id}`} onClick={() => setTab(t.id)} className={`shrink-0 px-4 py-2 text-sm font-bold border-b-2 transition-colors inline-flex items-center gap-1.5 ${tab === t.id ? "border-brand text-ink" : "border-transparent text-ink-soft hover:text-ink"}`}>
            {t.icon && <t.icon className="w-3.5 h-3.5"/>} {t.label}
          </button>
        ))}
      </nav>

      {tab === "browse" && (
        <>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {FILTERS.map(f => (
              <button key={f.id} data-testid={`adopt-filter-${f.id}`} onClick={() => setSp(f.id)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold border ${sp === f.id ? "bg-brand text-white border-brand" : "bg-surface border-line text-ink-soft"}`}>{f.label}</button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map(p => <BrowseCard key={p.listing_id} p={p} onApply={() => setApplying(p)}/>)}
            {items.length === 0 && <div className="col-span-full bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">No pets up for adoption right now. Be the first — click "List a pet".</div>}
          </div>
        </>
      )}

      {tab === "mine" && <MyListingsTab/>}
      {tab === "received" && <ApplicationsReceivedTab/>}
      {tab === "sent" && <ApplicationsSentTab/>}

      {creating && <ListPetModal prefill={prefill} onClose={() => { setCreating(false); setPrefill(null); loadBrowse(); }}/>}
      {applying && <ApplyModal listing={applying} onClose={() => { setApplying(null); loadBrowse(); }}/>}
    </div>
  );
}

function BrowseCard({ p, onApply }) {
  return (
    <article data-testid={`adopt-${p.listing_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 transition-all">
      <div className="relative h-48">
        <img src={p.photo} className="w-full h-full object-cover" alt={p.name}/>
        {p.vaccinated && <div className="absolute top-3 right-3 bg-leaf text-ink rounded-full px-3 py-1 text-[11px] font-bold inline-flex items-center gap-1"><Shield className="w-3 h-3"/>Vaccinated</div>}
        {p.status === "adopted" && <div className="absolute inset-0 bg-black/60 grid place-items-center text-white font-heading text-2xl font-extrabold">ADOPTED ♥</div>}
      </div>
      <div className="p-4">
        <h3 className="font-heading text-xl font-extrabold">{p.name}</h3>
        <div className="text-xs text-ink-soft mt-0.5 uppercase tracking-wider font-bold">{p.breed || p.species} · {p.age_years}y</div>
        <p className="text-sm text-ink-soft mt-2 line-clamp-2">{p.description}</p>
        <div className="text-xs text-ink-soft mt-2 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{p.city}</div>
        {p.status !== "adopted" && (
          <button onClick={onApply} data-testid={`apply-${p.listing_id}`} className="mt-3 w-full bg-brand text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand-hover inline-flex items-center justify-center gap-1.5">
            <Heart className="w-4 h-4"/> Apply to adopt
          </button>
        )}
      </div>
    </article>
  );
}

function MyListingsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { axios.get(`${API}/adoption/mine`).then(r => setItems(r.data.items)); }, []);
  if (items.length === 0) return <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">You haven't listed any pets for adoption yet.</div>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map(p => (
        <div key={p.listing_id} className="bg-surface border border-line rounded-3xl p-4">
          <div className="flex gap-3">
            <img src={p.photo} className="w-16 h-16 rounded-xl object-cover" alt=""/>
            <div className="flex-1">
              <div className="font-heading font-extrabold">{p.name}</div>
              <div className="text-xs text-ink-soft">{p.species} · {p.city}</div>
              <span className={`mt-1 inline-block text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${p.status === "available" ? "bg-leaf text-ink" : "bg-surface-muted"}`}>{p.status}</span>
            </div>
            <div className="text-center">
              <div className="text-2xl font-heading font-extrabold">{p.applications_count || 0}</div>
              <div className="text-[9px] uppercase tracking-wider font-bold text-ink-soft">apps</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApplicationsReceivedTab() {
  const [apps, setApps] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const load = () => axios.get(`${API}/adoption/applications/received`).then(r => setApps(r.data.items));
  useEffect(() => { load(); }, []);
  if (apps.length === 0) return <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">No applications yet.</div>;
  return (
    <div className="space-y-3">
      {apps.map(a => (
        <article key={a.application_id} data-testid={`recv-${a.application_id}`} className="bg-surface border border-line rounded-2xl p-4 flex flex-wrap items-start gap-3">
          {a.applicant_picture ? <img src={a.applicant_picture} className="w-12 h-12 rounded-full object-cover" alt=""/> : <div className="w-12 h-12 rounded-full bg-leaf grid place-items-center font-bold">{a.applicant_name?.[0]}</div>}
          <div className="flex-1 min-w-[200px]">
            <div className="font-heading font-extrabold">{a.applicant_name}</div>
            <p className="text-sm text-ink-soft mt-1">"{a.message}"</p>
            <div className="text-xs text-ink-soft mt-1.5">{a.home_type || "—"} · {a.has_other_pets ? "has pets" : "no other pets"} {a.phone && `· ${a.phone}`}</div>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${a.status === "approved" ? "bg-brand-green text-white" : a.status === "rejected" ? "bg-ink text-white" : "bg-sun text-ink"}`}>{a.status}</span>
          {a.status === "pending" && (
            <div className="flex gap-2 w-full sm:w-auto">
              <button data-testid={`approve-${a.application_id}`} onClick={() => setReviewing({ a, decision: "approve" })} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-bold">Approve</button>
              <button onClick={() => setReviewing({ a, decision: "reject" })} className="bg-surface-muted rounded-full px-3 py-1.5 text-xs font-bold">Reject</button>
            </div>
          )}
        </article>
      ))}
      {reviewing && <ReviewModal {...reviewing} onClose={() => { setReviewing(null); load(); }}/>}
    </div>
  );
}

function ReviewModal({ a, decision, onClose }) {
  const [phone, setPhone] = useState("");
  const submit = async () => {
    await axios.patch(`${API}/adoption/applications/${a.application_id}`, { decision, owner_phone: phone });
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md">
        <h3 className="font-heading text-xl font-extrabold">{decision === "approve" ? "Approve" : "Reject"} application</h3>
        <p className="text-sm text-ink-soft mt-1">By <b>{a.applicant_name}</b></p>
        {decision === "approve" && (
          <label className="block mt-4">
            <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">Your phone (revealed to applicant)</span>
            <input data-testid="owner-phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91-…" className="mt-1 w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-bold outline-none focus:border-brand"/>
          </label>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-surface-muted rounded-full py-2.5 font-bold">Cancel</button>
          <button data-testid="confirm-review" onClick={submit} className="flex-1 bg-brand text-white rounded-full py-2.5 font-bold">{decision === "approve" ? "Approve" : "Reject"}</button>
        </div>
      </div>
    </div>
  );
}

function ApplicationsSentTab() {
  const [apps, setApps] = useState([]);
  useEffect(() => { axios.get(`${API}/adoption/applications/sent`).then(r => setApps(r.data.items)); }, []);
  if (apps.length === 0) return <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">You haven't applied to adopt any pet yet.</div>;
  return (
    <div className="space-y-3">
      {apps.map(a => (
        <div key={a.application_id} className="bg-surface border border-line rounded-2xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-heading font-extrabold">Application · {a.listing_id.slice(-6)}</div>
            <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${a.status === "approved" ? "bg-brand-green text-white" : a.status === "rejected" ? "bg-ink text-white" : "bg-sun text-ink"}`}>{a.status}</span>
          </div>
          <p className="text-sm text-ink-soft mt-2">"{a.message}"</p>
          {a.status === "approved" && (a.owner_revealed_phone || a.owner_revealed_email) && (
            <div className="mt-3 bg-leaf/40 rounded-2xl p-3 text-sm">
              🎉 <b>Approved!</b> Owner's contact:
              {a.owner_revealed_phone && <div className="inline-flex items-center gap-1 ml-2"><Phone className="w-3 h-3"/>{a.owner_revealed_phone}</div>}
              {a.owner_revealed_email && <div className="inline-flex items-center gap-1 ml-2"><Mail className="w-3 h-3"/>{a.owner_revealed_email}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ListPetModal({ prefill, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: prefill?.name || "",
    species: prefill?.species || "dog",
    breed: prefill?.breed || "",
    age_years: prefill?.age_years ?? 1,
    gender: "male",
    description: "",
    photo: prefill?.photo || null,
    city: "",
    vaccinated: !!(prefill?.vaccinations?.length),
    sterilized: false,
    adoption_fee: 0,
    listing_type: user?.role === "ngo" ? "ngo" : (user?.role === "super_admin" ? "admin" : "parent"),
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/adoption/list`, form);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="list-pet-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-extrabold">List a pet for adoption</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        {prefill && (
          <div className="mb-4 bg-leaf/30 border border-leaf rounded-2xl px-3 py-2 text-xs font-bold inline-flex items-center gap-2" data-testid="prefill-banner">
            <Heart className="w-3.5 h-3.5 text-brand"/> Pre-filled from {prefill.name}'s profile — review the details below and add city + story before publishing.
          </div>
        )}
        <PhotoUpload value={form.photo} onChange={p => set("photo", p)} label="Pet photo *" testid="adopt-photo"/>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Name *"><input data-testid="adopt-name" required value={form.name} onChange={e => set("name", e.target.value)} className="inp"/></Field>
          <Field label="Species">
            <select value={form.species} onChange={e => set("species", e.target.value)} className="inp">
              <option value="dog">Dog</option><option value="cat">Cat</option><option value="rabbit">Rabbit</option><option value="bird">Bird</option><option value="other">Other</option>
            </select>
          </Field>
          <Field label="Breed"><input value={form.breed} onChange={e => set("breed", e.target.value)} className="inp"/></Field>
          <Field label="Age (years)"><input type="number" step="0.5" min="0" value={form.age_years} onChange={e => set("age_years", Number(e.target.value))} className="inp"/></Field>
          <Field label="Gender">
            <select value={form.gender} onChange={e => set("gender", e.target.value)} className="inp">
              <option value="male">Male</option><option value="female">Female</option>
            </select>
          </Field>
          <Field label="City *"><input required value={form.city} onChange={e => set("city", e.target.value)} className="inp"/></Field>
        </div>
        <Field label="Story / personality *">
          <textarea required rows={3} value={form.description} onChange={e => set("description", e.target.value)} className="inp"/>
        </Field>
        <div className="flex flex-wrap gap-4 mt-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.vaccinated} onChange={e => set("vaccinated", e.target.checked)}/> Vaccinated</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.sterilized} onChange={e => set("sterilized", e.target.checked)}/> Sterilized</label>
          <label className="flex items-center gap-2 text-sm">Adoption fee (₹) <input type="number" min="0" value={form.adoption_fee} onChange={e => set("adoption_fee", Number(e.target.value))} className="inp w-24"/></label>
        </div>
        <button data-testid="submit-listing" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold">Publish listing</button>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

function ApplyModal({ listing, onClose }) {
  const [form, setForm] = useState({ message: "", home_type: "apartment", has_other_pets: false, days_available: 365, phone: "", id_proof_url: null, home_photo_url: null });
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/adoption/apply`, { listing_id: listing.listing_id, ...form });
    setDone(true);
  };
  if (done) return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-8 max-w-md text-center" data-testid="apply-success">
        <Heart className="w-12 h-12 text-brand fill-brand mx-auto"/>
        <h3 className="font-heading text-xl font-extrabold mt-3">Application sent!</h3>
        <p className="text-sm text-ink-soft mt-2">The owner will review and get back to you within 48 hours.</p>
        <button onClick={onClose} className="mt-4 bg-ink text-white rounded-full px-6 py-2 text-sm font-bold">Close</button>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="apply-form">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Adopt {listing.name}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <p className="text-xs text-ink-soft inline-flex items-center gap-1 mb-3"><Hourglass className="w-3 h-3"/> Review typically takes 48 hours.</p>
        <Field label="Why you'd be a great home *"><textarea required rows={3} value={form.message} onChange={e => set("message", e.target.value)} className="inp" data-testid="apply-message"/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Home type">
            <select value={form.home_type} onChange={e => set("home_type", e.target.value)} className="inp">
              <option value="apartment">Apartment</option><option value="independent">Independent</option><option value="farm">Farm</option>
            </select>
          </Field>
          <Field label="Phone (WhatsApp)"><input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+91-…" className="inp"/></Field>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm"><input type="checkbox" checked={form.has_other_pets} onChange={e => set("has_other_pets", e.target.checked)}/> I have other pets</label>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <PhotoUpload value={form.id_proof_url} onChange={p => set("id_proof_url", p)} label="ID proof" testid="apply-id" helpText="Aadhar / PAN"/>
          <PhotoUpload value={form.home_photo_url} onChange={p => set("home_photo_url", p)} label="Home photo" testid="apply-home"/>
        </div>
        <button data-testid="submit-application" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold">Submit application</button>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.55rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-3">
      <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
